import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

const ManageSubscription = ({ token }) => {
    const [sub, setSub] = useState(null);
    const [saasPlans, setSaasPlans] = useState([]);
    const [isYearlyBilling, setIsYearlyBilling] = useState(true);
    const [loading, setLoading] = useState(true);
    const [processing, setProcessing] = useState('');
    
    // Coupon state
    const [couponCode, setCouponCode] = useState('');
    const [appliedCoupon, setAppliedCoupon] = useState(null);
    const [discountInfo, setDiscountInfo] = useState(null);
    const [couponMsg, setCouponMsg] = useState({text: '', type: ''});
    const [applyingCoupon, setApplyingCoupon] = useState(false);

    const [trialDays, setTrialDays] = useState(7);
    const [lowestPlan, setLowestPlan] = useState({ name: 'Basic', price: 11 });

    const [earnedCredits, setEarnedCredits] = useState(0);
    const [applyCredits, setApplyCredits] = useState(false);

    // History Modal State
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [historyData, setHistoryData] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    useEffect(() => {
        fetchSub();
        // Dynamically load the Cashfree SDK if not loaded
        if (!window.Cashfree) {
            const script = document.createElement('script');
            script.src = "https://sdk.cashfree.com/js/v3/cashfree.js";
            script.async = true;
            document.body.appendChild(script);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchSub = async () => {
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_subscription.php?action=fetch', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setSub(data.subscription);
            }

            const plansRes = await fetch('https://menu.searchisend.in/apimenu/v2/v2_public_plans.php');
            const plansData = await plansRes.json();
            if (plansData.success && plansData.plans) {
                const activePlans = plansData.plans.filter(p => p.is_monthly_active === 1 || p.is_yearly_active === 1);
                setSaasPlans(activePlans);
                setTrialDays(plansData.trial_days || 7);
                if (activePlans.length > 0) {
                    setLowestPlan({ name: activePlans[0].plan_name, price: activePlans[0].price_monthly });
                }
            }

            // Fetch earned credits
            const fd = new FormData();
            fd.append('action', 'get_monetization_status');
            const credRes = await fetch('https://menu.searchisend.in/apimenu/v2/v2_restaurant_monetization.php', {
                method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: fd
            });
            const credData = await credRes.json();
            if (credData.success && credData.earnings) {
                setEarnedCredits(parseFloat(credData.earnings));
            }

        } catch (err) { console.error(err); } finally { setLoading(false); }
    };

    const fetchHistory = async () => {
        setShowHistoryModal(true);
        setLoadingHistory(true);
        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_payment_history.php', {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setHistoryData(data.payments);
            } else {
                toast.error(data.message || 'Failed to fetch history');
            }
        } catch (error) {
            console.error("Error fetching history:", error);
            toast.error("Network error. Could not fetch history.");
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleCheckout = async (planName, planPrice) => {
        if (!window.Cashfree) {
            toast.error('Payment Gateway is still loading. Please try again in 5 seconds.');
            return;
        }

        setProcessing(planName);
        toast.success('Initializing payment... please wait.');

        try {
            const res = await fetch('https://menu.searchisend.in/apimenu/v2/v2_create_payment.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ 
                    plan_name: planName, 
                    plan_price: planPrice,
                    coupon_code: appliedCoupon,
                    apply_credits: applyCredits
                })
            });

            const data = await res.json();
            
            if (!res.ok || !data.success) {
                throw new Error(data.message || 'Payment initiation failed.');
            }

            if (data.bypassed) {
                toast.success('Success! Your free plan has been activated.');
                setSub(prev => ({...prev, status: 'Active'}));
                setTimeout(() => window.location.reload(), 2000);
                return;
            }

            const cashfree = new window.Cashfree({ mode: "production" });
            
            cashfree.checkout({
                paymentSessionId: data.payment_session_id,
                returnUrl: data.return_url
            }).then((result) => {
                if (result.error) {
                    toast.error(`Payment Cancelled or Failed: ${result.error.message}`);
                }
            });
        } catch (err) {
            toast.error(err.message);
        } finally {
            setProcessing('');
        }
    };

    const handleApplyCoupon = async () => {
        if (!couponCode.trim()) return;
        setApplyingCoupon(true);
        setCouponMsg({text: '', type: ''});
        try {
            const res = await fetch(`https://menu.searchisend.in/apimenu/v2/v2_validate_saas_coupon.php?code=${couponCode.trim()}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.success) {
                setAppliedCoupon(couponCode.trim().toUpperCase());
                setDiscountInfo({ 
                    type: data.discount_type, 
                    value: data.discount_value,
                    applicable_plan_name: data.applicable_plan_name,
                    applicable_cycle: data.applicable_cycle
                });
                setCouponMsg({text: `Coupon applied successfully!`, type: 'success'});
            } else {
                setAppliedCoupon(null);
                setDiscountInfo(null);
                setCouponMsg({text: data.message || 'Invalid coupon.', type: 'error'});
            }
        } catch (err) {
            setCouponMsg({text: 'Error verifying coupon.', type: 'error'});
        } finally {
            setApplyingCoupon(false);
        }
    };

    const removeCoupon = () => {
        setAppliedCoupon(null);
        setDiscountInfo(null);
        setCouponCode('');
        setCouponMsg({text: '', type: ''});
    };

    if (loading) return <div className="admin-card">Loading Subscription Details...</div>;
    if (!sub) return null;

    const isActive = sub.status === 'Active';

    return (
        <div className="admin-card">
            <Link to="/manage_v2_secure_auth/" className="sticky-back-btn">
    <i className="fas fa-arrow-left"></i> Back to Dashboard
</Link>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <h1 style={{marginTop: '0', marginBottom: '0'}}>My Subscription</h1>
                <button 
                    onClick={fetchHistory}
                    style={{
                        padding: '8px 16px',
                        background: '#f8f9fa',
                        color: 'var(--text-color)',
                        border: '1px solid #e5e7eb',
                        borderRadius: '50px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        transition: 'all 0.2s',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.05)'
                    }}
                    onMouseOver={e => e.currentTarget.style.background = '#e5e7eb'}
                    onMouseOut={e => e.currentTarget.style.background = '#f8f9fa'}
                >
                    <i className="fas fa-history"></i> View History
                </button>
            </div>


            <h2 style={{fontSize: '1.4rem'}}>Current Status</h2>
            <div style={{padding: '20px', borderRadius: '10px', marginBottom: '25px', fontSize: '1.1em', fontWeight: '500', backgroundColor: isActive ? 'var(--light-orange)' : '#e2e3e5', color: isActive ? 'var(--tag-text-color)' : '#383d41', border: `1px solid ${isActive ? 'var(--primary-orange)' : '#d6d8db'}`}}>
                {sub.status === 'Expired' ? (
                    <>
                       Your {trialDays}-day free trial / subscription has <strong>EXPIRED</strong>.<br />
                       <span style={{fontSize: '0.9em', color: '#dc2626', fontWeight: '500'}}>Please upgrade to our {lowestPlan.name} Plan (₹{lowestPlan.price}/mo) or a Pro Plan below to restore full dashboard access and keep your digital menu active.</span>
                    </>
                ) : (
                    <>
                        Your account is currently <strong style={{textTransform: 'uppercase'}}>{sub.status}</strong>.<br />
                        Your subscription is valid until: <strong>{sub.renewal_date}</strong>
                    </>
                )}
            </div>

            {sub.custom_discount_percent > 0 && (
                <div style={{padding: '15px 20px', borderRadius: '10px', marginBottom: '25px', display: 'flex', alignItems: 'center', gap: '15px', backgroundColor: 'var(--light-orange)', border: '1px solid var(--primary-orange)'}}>
                    <i className="fas fa-gift" style={{fontSize: '1.5rem', color: 'var(--primary-orange)'}}></i>
                    <div>
                        <strong style={{display: 'block', fontSize: '1.1rem', color: 'var(--primary-orange)'}}>Special Offer Applied!</strong>
                        <span style={{color: 'var(--text-color)'}}>Your account has a lifetime <strong>{sub.custom_discount_percent}% discount</strong> applied to all subscription plans below.</span>
                    </div>
                </div>
            )}

            <h2 style={{fontSize: '1.4rem'}}>Choose a Renewal Plan</h2>
            
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                <div style={{ display: 'inline-flex', background: '#e2e8f0', padding: '4px', borderRadius: '30px', alignItems: 'center' }}>
                    <button 
                        onClick={() => setIsYearlyBilling(false)}
                        style={{ padding: '8px 20px', borderRadius: '26px', border: 'none', background: !isYearlyBilling ? '#fff' : 'transparent', color: !isYearlyBilling ? 'var(--text-color)' : '#64748b', fontWeight: 600, cursor: 'pointer', boxShadow: !isYearlyBilling ? '0 2px 8px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.3s' }}
                    >Monthly</button>
                    <button 
                        onClick={() => setIsYearlyBilling(true)}
                        style={{ padding: '8px 20px', borderRadius: '26px', border: 'none', background: isYearlyBilling ? '#fff' : 'transparent', color: isYearlyBilling ? 'var(--text-color)' : '#64748b', fontWeight: 600, cursor: 'pointer', boxShadow: isYearlyBilling ? '0 2px 8px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.3s', display: 'flex', alignItems: 'center', gap: '5px' }}
                    >Yearly <span style={{ background: '#10b981', color: '#fff', fontSize: '0.65rem', padding: '2px 6px', borderRadius: '10px' }}>Save 30%</span></button>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '30px' }}>
                <div style={{ background: 'var(--bg-color)', padding: '15px 25px', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', maxWidth: '500px', width: '100%', boxShadow: '0 4px 6px rgba(0,0,0,0.05)' }}>
                    <i className="fas fa-ticket-alt" style={{ color: 'var(--primary-orange)' }}></i>
                    <input 
                        type="text" 
                        placeholder="Enter Promo Code" 
                        value={couponCode} 
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        disabled={appliedCoupon !== null}
                        style={{ flexGrow: 1, padding: '10px', borderRadius: '6px', border: '1px solid #ccc', background: appliedCoupon ? '#f0f0f0' : '#fff', color: '#333' }}
                    />
                    {appliedCoupon ? (
                        <button onClick={removeCoupon} style={{ padding: '10px 15px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}>Remove</button>
                    ) : (
                        <button onClick={handleApplyCoupon} disabled={applyingCoupon} style={{ padding: '10px 20px', background: 'var(--primary-orange)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', opacity: applyingCoupon ? 0.7 : 1 }}>
                            {applyingCoupon ? 'Applying...' : 'Apply'}
                        </button>
                    )}
                    {couponMsg.text && (
                        <div style={{ width: '100%', fontSize: '0.9rem', marginTop: '5px', color: couponMsg.type === 'success' ? '#10b981' : '#dc2626' }}>
                            {couponMsg.text}
                        </div>
                    )}
                </div>
            </div>

            {earnedCredits > 0 && (
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
                    <label style={{ cursor: 'pointer', background: 'var(--bg-color)', padding: '15px 25px', borderRadius: '12px', border: '1px solid var(--border-color)', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', maxWidth: '500px', width: '100%', fontWeight: '600' }}>
                        <input 
                            type="checkbox" 
                            checked={applyCredits} 
                            onChange={(e) => setApplyCredits(e.target.checked)} 
                            style={{ width: '20px', height: '20px', accentColor: 'var(--primary-orange)' }}
                        />
                        Apply Earned Credits (Wallet Balance: ₹{earnedCredits})
                        <span style={{ marginLeft: 'auto', fontSize: '0.85rem', color: '#64748b' }}>Up to 50% off</span>
                    </label>
                </div>
            )}

            <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px', marginTop: '20px'}}>
                {saasPlans.filter(p => isYearlyBilling ? p.is_yearly_active === 1 : p.is_monthly_active === 1).map((plan) => {
                    const basePrice = isYearlyBilling ? plan.price_yearly : plan.price_monthly;
                    let price = basePrice;
                    
                    let isApplicable = false;
                    let totalDiscountPercent = sub?.custom_discount_percent || 0;
                    let flatDiscountAmount = 0;

                    if (appliedCoupon && discountInfo) {
                        isApplicable = true;
                        if (discountInfo.applicable_plan_name !== 'ALL' && discountInfo.applicable_plan_name !== plan.plan_name) {
                            isApplicable = false;
                        }
                        if (discountInfo.applicable_cycle !== 'ALL') {
                            if (discountInfo.applicable_cycle === 'Monthly' && isYearlyBilling) isApplicable = false;
                            if (discountInfo.applicable_cycle === 'Yearly' && !isYearlyBilling) isApplicable = false;
                        }

                        if (isApplicable) {
                            if (discountInfo.type === 'percent') {
                                totalDiscountPercent += discountInfo.value;
                            } else {
                                flatDiscountAmount += discountInfo.value;
                            }
                        }
                    }

                    let totalDiscountAmount = flatDiscountAmount;
                    if (totalDiscountPercent > 0) {
                        totalDiscountAmount += basePrice * (Math.min(100, totalDiscountPercent) / 100);
                    }

                    price = Math.max(0, basePrice - totalDiscountAmount);

                    let applicableCredits = 0;
                    if (applyCredits) {
                        const maxCreditDiscount = price * 0.50;
                        applicableCredits = Math.min(earnedCredits, maxCreditDiscount);
                        price = Math.max(0, price - applicableCredits);
                    }

                    const durationDays = isYearlyBilling ? 365 : 30;
                    const encodedName = `${plan.plan_name} | ID:${plan.id} | DAYS:${durationDays}`;

                    return (
                        <div key={plan.id} style={{border: `2px solid ${plan.is_popular === 1 ? 'var(--primary-orange)' : 'var(--border-color)'}`, position: 'relative', padding: '25px', borderRadius: '10px', textAlign: 'center', backgroundColor: 'var(--bg-color)', display: 'flex', flexDirection: 'column'}}>
                            {plan.is_popular === 1 && (
                                <div style={{ position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', background: 'var(--primary-orange)', color: '#fff', padding: '2px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                                    MOST POPULAR
                                </div>
                            )}
                            <h3 style={{fontSize: '1.3em', marginTop: 0}}>{plan.plan_name}</h3>
                            <div style={{fontSize: 'clamp(1.8rem, 5vw, 2.2rem)', fontWeight: '700', color: 'var(--primary-orange)', margin: '15px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}>
                                {price < basePrice ? (
                                    <>
                                        <span style={{ textDecoration: 'line-through', fontSize: '0.45em', color: '#999', marginBottom: '-2px' }}>₹{basePrice}</span>
                                        <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                            <span style={{ color: '#10b981' }}>₹{price}</span>
                                            <span style={{fontSize: '0.45em', fontWeight: '500', color: 'var(--text-color-secondary)', marginLeft: '4px'}}>/{isYearlyBilling ? 'year' : 'month'}</span>
                                        </div>
                                    </>
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'baseline' }}>
                                        <span>₹{price}</span>
                                        <span style={{fontSize: '0.45em', fontWeight: '500', color: 'var(--text-color-secondary)', marginLeft: '4px'}}>/{isYearlyBilling ? 'year' : 'month'}</span>
                                    </div>
                                )}
                            </div>
                            
                            {appliedCoupon && !isApplicable && (
                                <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600, marginBottom: '10px' }}>
                                    Coupon not applicable for this plan/cycle.
                                </div>
                            )}

                            {applyCredits && applicableCredits > 0 && (
                                <div style={{ fontSize: '0.85rem', color: '#10b981', fontWeight: 600, marginBottom: '10px' }}>
                                    Credits Applied: -₹{applicableCredits.toFixed(2)}
                                </div>
                            )}
                            
                            <ul style={{listStyle: 'none', padding: 0, margin: '20px 0', textAlign: 'left', flexGrow: 1}}>
                                {plan.features_included.map((f, i) => (
                                    <li key={`inc-${i}`} style={{marginBottom: '10px'}}><i className="fas fa-check" style={{color: 'var(--green-accent)', marginRight: '8px'}}></i> {f}</li>
                                ))}
                                {plan.features_excluded.map((f, i) => (
                                    <li key={`exc-${i}`} style={{color: '#999', marginBottom: '10px'}}><i className="fas fa-times" style={{color: 'var(--red-accent)', marginRight: '8px'}}></i> {f}</li>
                                ))}
                            </ul>
                            
                            <button onClick={() => handleCheckout(encodedName, price)} disabled={processing === encodedName} style={{width: '100%', padding: '12px', backgroundColor: plan.is_popular === 1 ? 'var(--primary-orange)' : 'transparent', color: plan.is_popular === 1 ? 'white' : 'var(--text-color)', border: plan.is_popular === 1 ? 'none' : '2px solid var(--border-color)', borderRadius: '50px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s', ...(processing === encodedName ? {opacity: 0.7} : {})}}>
                                {processing === encodedName ? 'Processing...' : 'Choose Plan'}
                            </button>
                        </div>
                    );
                })}
            </div>

            {showHistoryModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', boxSizing: 'border-box' }}>
                    <div style={{ background: '#fff', borderRadius: '16px', width: '100%', maxWidth: '600px', maxHeight: '80vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.2)' }}>
                        <div style={{ padding: '20px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8f9fa' }}>
                            <h2 style={{ margin: 0, fontSize: '1.4rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <i className="fas fa-file-invoice-dollar" style={{ color: 'var(--primary-orange)' }}></i> Payment History
                            </h2>
                            <button onClick={() => setShowHistoryModal(false)} style={{ background: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}>&times;</button>
                        </div>
                        <div style={{ padding: '20px', overflowY: 'auto', flexGrow: 1 }}>
                            {loadingHistory ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    {[1, 2, 3].map(i => (
                                        <div key={i} style={{ height: '70px', background: '#f3f4f6', borderRadius: '8px', animation: 'pulse 1.5s infinite' }}></div>
                                    ))}
                                </div>
                            ) : historyData.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <i className="fas fa-receipt" style={{ fontSize: '3rem', color: '#cbd5e1', marginBottom: '15px' }}></i>
                                    <p style={{ color: '#64748b', fontSize: '1.1rem', margin: 0 }}>No payment history found.</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    {historyData.map((txn) => {
                                        let statusColor = '#f59e0b';
                                        if (txn.payment_status === 'Paid') statusColor = '#10b981';
                                        if (txn.payment_status === 'Failed' || txn.payment_status === 'Cancelled') statusColor = '#dc2626';

                                        return (
                                            <div key={txn.payment_id} style={{ border: '1px solid #e5e7eb', borderRadius: '10px', padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                                                <div>
                                                    <strong style={{ display: 'block', fontSize: '1.1rem', color: '#1f2937' }}>{txn.plan_name}</strong>
                                                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>{new Date(txn.created_at).toLocaleString()}</span>
                                                </div>
                                                <div style={{ textAlign: 'right' }}>
                                                    <strong style={{ display: 'block', fontSize: '1.2rem', color: '#1f2937' }}>₹{txn.amount}</strong>
                                                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: statusColor, background: `${statusColor}20`, padding: '2px 8px', borderRadius: '12px' }}>
                                                        {txn.payment_status}
                                                    </span>
                                                </div>
                                                {txn.cashfree_order_id && txn.cashfree_order_id !== 'FREE_COUPON' && (
                                                    <div style={{ width: '100%', fontSize: '0.75rem', color: '#9ca3af', marginTop: '5px' }}>
                                                        Ref: {txn.cashfree_order_id}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <style>{`
                            @keyframes pulse {
                                0% { opacity: 1; }
                                50% { opacity: 0.5; }
                                100% { opacity: 1; }
                            }
                        `}</style>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManageSubscription;
